// ReglasGastosPanel.js — reglas de clasificación de gastos
// (config_reglas_gastos): un patrón de texto buscado dentro del concepto del
// movimiento bancario → una categoría. Se evalúan en el orden en que se
// crearon — la primera que coincida gana. Solo Material usa el % de IVA
// (resta la base imponible del importe pagado; el resto de categorías cuenta
// el importe completo pagado — criterio de caja).
import React, { useState, useEffect, useCallback } from 'react';
import { authService } from '../../services/authService';

export const CATEGORIAS_GASTO = [
  { key: 'material', label: 'Material' },
  { key: 'personal', label: 'Personal' },
  { key: 'alquiler', label: 'Alquiler' },
  { key: 'vehiculos', label: 'Vehículos' },
  { key: 'suministros', label: 'Suministros' },
  { key: 'seguros', label: 'Seguros' },
  { key: 'software', label: 'Software' },
  { key: 'asesoria', label: 'Asesoría' },
  { key: 'otros_gastos', label: 'Otros gastos' },
  { key: 'direccion', label: 'Dirección' },
  { key: 'iva', label: 'IVA (modelo 303)' },
  { key: 'irpf_titular', label: 'IRPF del titular' },
  { key: 'inversion', label: 'Inversión' },
  { key: 'traspaso_interno', label: 'Traspaso interno' },
];

const EMPTY = { id: null, patron: '', categoria: 'material', ivaPct: '21' };

export default function ReglasGastosPanel() {
  const [reglas, setReglas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [form, setForm] = useState(EMPTY);
  const [guardando, setGuardando] = useState(false);

  const cargar = useCallback(() => {
    setLoading(true); setError('');
    fetch('/.netlify/functions/getReglasGasto', { headers: { ...authService.authHeader() } })
      .then(r => r.json())
      .then(data => { if (!data.success) { setError(data.error); return; } setReglas(data.reglas); })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const handleGuardar = async () => {
    if (!form.patron.trim()) return;
    setGuardando(true);
    try {
      const res = await fetch('/.netlify/functions/saveReglaGasto', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
        body: JSON.stringify({
          id: form.id || undefined, patron: form.patron, categoria: form.categoria,
          ivaPct: form.categoria === 'material' ? Number(form.ivaPct || 21) : undefined,
        }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error);
      setForm(EMPTY);
      cargar();
    } catch (e) {
      setError(e.message);
    } finally {
      setGuardando(false);
    }
  };

  const handleEliminar = async (id) => {
    if (!window.confirm('¿Eliminar esta regla? Los movimientos que ya clasificó no cambian retroactivamente hasta el próximo recálculo.')) return;
    try {
      const res = await fetch('/.netlify/functions/saveReglaGasto', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
        body: JSON.stringify({ accion: 'eliminar', id }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error);
      cargar();
    } catch (e) {
      setError(e.message);
    }
  };

  if (loading) return <div className="flex justify-center py-10"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-red-500" /></div>;

  return (
    <div className="space-y-4">
      {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-2 text-sm">{error}</div>}

      <div className="bg-white rounded-2xl border shadow-sm p-5">
        <h3 className="font-bold text-gray-900 text-sm mb-1">Añadir regla</h3>
        <p className="text-[11px] text-gray-400 mb-3">Se busca el patrón dentro del concepto del movimiento (sin distinguir mayúsculas ni acentos). La primera regla que coincida, de arriba abajo en el histórico, gana.</p>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-2">
          <input
            className="col-span-2 border rounded-lg px-3 py-2 text-sm"
            placeholder='Patrón (ej. "Saltoki", "TGSS")'
            value={form.patron} onChange={e => setForm(f => ({ ...f, patron: e.target.value }))}
          />
          <select
            className="border rounded-lg px-3 py-2 text-sm"
            value={form.categoria} onChange={e => setForm(f => ({ ...f, categoria: e.target.value }))}
          >
            {CATEGORIAS_GASTO.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
          {form.categoria === 'material' && (
            <input
              type="number" className="border rounded-lg px-3 py-2 text-sm" placeholder="% IVA"
              value={form.ivaPct} onChange={e => setForm(f => ({ ...f, ivaPct: e.target.value }))}
            />
          )}
          <button
            onClick={handleGuardar} disabled={guardando}
            className="px-3 py-2 bg-red-600 text-white text-xs font-bold rounded-lg hover:bg-red-700 disabled:opacity-60"
          >
            {guardando ? 'Guardando…' : (form.id ? 'Guardar cambios' : 'Añadir')}
          </button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border shadow-sm overflow-hidden">
        <div className="px-5 py-3 border-b bg-gray-50">
          <h3 className="font-bold text-sm text-gray-900">Reglas (orden de aplicación)</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b bg-gray-50/60">
                <th className="text-left px-4 py-2 font-bold text-gray-500 uppercase">#</th>
                <th className="text-left px-3 py-2 font-bold text-gray-500 uppercase">Patrón</th>
                <th className="text-left px-3 py-2 font-bold text-gray-500 uppercase">Categoría</th>
                <th className="text-right px-3 py-2 font-bold text-gray-500 uppercase">IVA</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {reglas.map((r, i) => (
                <tr key={r.id}>
                  <td className="px-4 py-2 text-gray-400">{i + 1}</td>
                  <td className="px-3 py-2 font-mono font-semibold text-gray-700">{r.patron}</td>
                  <td className="px-3 py-2 text-gray-500">{CATEGORIAS_GASTO.find(c => c.key === r.categoria)?.label || r.categoria}</td>
                  <td className="px-3 py-2 text-right text-gray-400">{r.categoria === 'material' ? `${r.ivaPct ?? 21}%` : '—'}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <button onClick={() => setForm({ id: r.id, patron: r.patron, categoria: r.categoria, ivaPct: String(r.ivaPct ?? 21) })} className="text-blue-600 hover:underline mr-2">Editar</button>
                    <button onClick={() => handleEliminar(r.id)} className="text-red-600 hover:underline">Eliminar</button>
                  </td>
                </tr>
              ))}
              {reglas.length === 0 && (
                <tr><td colSpan={5} className="text-center py-6 text-gray-400">Sin reglas todavía — todo caerá en "Sin clasificar"</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
